/**
 * TMShell — mobile-first app shell for the /tm module.
 * Bottom tab bar on mobile, sidebar on desktop.
 */
import { NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, CheckSquare, FolderOpen, Users, User, LogOut } from 'lucide-react';
import { signOut } from 'firebase/auth';
import { auth } from '@/config/firebase';
import { useTMAuth } from '@/contexts/TMAuthContext';
import { cn } from '@/utils/cn';

const NAV = [
  { to: '/tm/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/tm/tasks',     label: 'Tasks',     icon: CheckSquare },
  { to: '/tm/projects',  label: 'Projects',  icon: FolderOpen },
  { to: '/tm/users',     label: 'Members',   icon: Users, adminOnly: true },
  { to: '/tm/profile',   label: 'Profile',   icon: User },
];

export default function TMShell({ children }: { children: React.ReactNode }) {
  const { tmUser, isTMAdmin } = useTMAuth();
  const navigate = useNavigate();

  const visibleNav = NAV.filter(n => !n.adminOnly || isTMAdmin);

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/tm');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">

      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-30 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/angels-army-logo.png" alt="" className="w-7 h-7 object-contain" />
            <span className="font-bold text-gray-900 text-sm">Angels Army TM</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-xs font-semibold text-gray-800 leading-none">{tmUser?.name}</p>
              <p className="text-[10px] text-gray-400 capitalize mt-0.5">{tmUser?.role}</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      {/* ── Page content ────────────────────────────────────────────────── */}
      <main className="flex-1 max-w-2xl mx-auto w-full px-4 py-5 pb-24">
        {children}
      </main>

      {/* ── Bottom tab bar (mobile) ──────────────────────────────────────── */}
      <nav className="fixed bottom-0 inset-x-0 z-30 bg-white border-t border-gray-100 safe-area-pb">
        <div className="max-w-2xl mx-auto flex">
          {visibleNav.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  'flex-1 flex flex-col items-center justify-center gap-1 py-3 text-[10px] font-medium transition-colors',
                  isActive ? 'text-indigo-600' : 'text-gray-400 hover:text-gray-600',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <Icon className={cn('h-5 w-5', isActive && 'text-indigo-600')} />
                  {label}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
